import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { toast } from "sonner";
import { DicomViewer } from "./DicomViewer";
import { ReconcileStudy } from "./ReconcileStudy";

export function StudyDetail({ ingestId, onClose, onChanged }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null); // {object_id, text}
  const [recOpen, setRecOpen] = useState(false);
  const [viewer, setViewer] = useState(false);

  const load = () => api.get(`/pam-connect/studies/${ingestId}`).then(({ data }) => setD(data)).catch(() => toast.error(t("diag.load_err")));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ingestId]);

  const openInstance = async (obj) => {
    setPreview({ object_id: obj.object_id, text: t("diag.loading") });
    try {
      const res = await api.get(`/pam-connect/studies/${ingestId}/instances/${obj.object_id}/file`, { responseType: "blob" });
      const size = res.data.size;
      // I file DICOM sintetici possono non contenere pixel data: nessuna anteprima immagine artificiale.
      setPreview({ object_id: obj.object_id, text: t("diag.no_pixels", { size }) });
    } catch { setPreview({ object_id: obj.object_id, text: t("diag.inst_err") }); }
  };

  const deliver = async () => {

    try { const { data } = await api.post(`/pam-connect/studies/${ingestId}/deliver`);
      toast.success(t("diag.deliver_ok", { n: data.delivered_objects })); load(); onChanged && onChanged(); }
    catch (e) {
      const code = e.response?.data?.detail || e.response?.data?.code;
      toast.error(code === "report_not_published" ? t("diag.deliver_lock") : code === "not_associated" ? t("diag.deliver_need_assoc") : t("diag.deliver_err"));
    }
  };

  return (<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="study-detail">
      {!d ? <div className="p-8 text-center text-slate-400">{t("diag.loading")}</div> : <>
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-bold" data-testid="study-detail-patient">{d.patient_name || t("diag.unknown_patient")}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{d.modality || "—"} · {t("diag.c_accession")} {d.accession_number || "—"} · {d.study_date ? new Date(d.study_date).toLocaleString() : "—"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="study-detail-close">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Stato + azioni */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid="study-detail-status">{t(`diag.st_${d.status}`)}</span>
            {d.series.length > 0 && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setViewer(true)} data-testid="study-open-viewer">{t("diag.open_viewer")}</button>}
            {d.report_published && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{t("diag.report_ok")}</span>}
            {d.status === "needs_association" && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setRecOpen(true)} data-testid="study-associate-btn">{t("diag.associate")}</button>}
            {d.patient_id && !d.delivered && <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" onClick={deliver} data-testid="study-deliver-btn">{t("diag.deliver")}</button>}
            {d.delivered && <span className="text-xs text-emerald-600" data-testid="study-delivered">{t("diag.delivered_note")}</span>}
          </div>
          {d.status === "needs_association" && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="study-protected-note">{t("diag.protected_note")}</div>}

          {/* Serie e istanze */}
          <div>
            <h4 className="font-semibold text-sm mb-2">{t("diag.series")} ({d.series.length})</h4>
            <div className="space-y-2">
              {d.series.map((se) => (
                <div key={se.series_uid} className="border border-slate-200 rounded-lg p-2" data-testid="study-series">
                  <div className="text-xs text-slate-500 mb-1">{t("diag.series")} …{(se.series_uid || "").slice(-10)} · {se.instance_count} {t("diag.instances")}</div>
                  <div className="flex flex-wrap gap-2">
                    {se.instances.map((ins) => (
                      <button key={ins.object_id} className="text-xs border border-slate-200 rounded px-2 py-1 hover:bg-blue-50" onClick={() => openInstance(ins)} data-testid="study-instance-btn">
                        …{(ins.sop_uid || ins.object_id).slice(-8)} · {ins.size_bytes} B
                      </button>))}
                  </div>
                </div>))}
            </div>
            {preview && <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-600" data-testid="study-instance-preview">{preview.text}</div>}
          </div>

          {/* Metadati DICOM reali */}
          <div>
            <h4 className="font-semibold text-sm mb-2">{t("diag.dicom_info")}</h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="study-dicom-info">
              <dt className="text-slate-400">{t("diag.c_patient")} (DICOM)</dt><dd>{d.dicom.patient_name || "—"}</dd>
              <dt className="text-slate-400">{t("diag.c_dicom_id")}</dt><dd>{d.dicom.source_patient_id || "—"}</dd>
              <dt className="text-slate-400">StudyInstanceUID</dt><dd className="break-all">{d.study_uid || "—"}</dd>
              <dt className="text-slate-400">{t("diag.c_accession")}</dt><dd>{d.accession_number || "—"}</dd>
              <dt className="text-slate-400">{t("diag.c_modality")}</dt><dd>{d.modality || "—"}</dd>
              <dt className="text-slate-400">{t("diag.c_images")}</dt><dd>{d.instance_count}</dd>
            </dl>
            <p className="text-[11px] text-slate-400 mt-2" data-testid="study-viewer-note">{t("diag.viewer_note")}</p>
          </div>

          {/* Documenti / referti */}
          {d.documents.length > 0 && <div>
            <h4 className="font-semibold text-sm mb-2">{t("diag.c_docs")} ({d.documents.length})</h4>
            <ul className="text-sm space-y-1">{d.documents.map((doc) => (
              <li key={doc.doc_id} className="flex justify-between"><span>{doc.title || "—"}</span><span className="text-xs text-slate-400">{t(`diag.doc_${doc.status}`)}</span></li>))}</ul>
          </div>}
        </div>
      </>}

      {viewer && d && <DicomViewer ingestId={ingestId} series={d.series} folderId={d.folder_id} onClose={() => setViewer(false)} onSaved={load} />}
      {recOpen && d && <ReconcileStudy study={{ ingest_id: ingestId, internal_patient_id: d.internal_patient_id, modality: d.modality, dicom_patient_name: d.dicom?.patient_name || d.patient_name, accession_number: d.accession_number }} onClose={() => setRecOpen(false)} onDone={() => { load(); onChanged && onChanged(); }} />}
    </div>
  </div>);
}
